// frontend/src/features/adhesives/AdhesivesForm.tsx
import React, { useState } from "react";
import { Calculator, AlertCircle } from "lucide-react";

export function AdhesivesForm({ adhesives, onChecked }: { adhesives: any[]; onChecked: (d:any)=>void }) {
  const [form, setForm] = useState<any>({
    adhesiveId: "",
    materials: ["PMMA","PMMA"],
    thickness_mm: 4,
    bond_width_mm: 200,
    overlap_length_mm: 20,
    load_N: 120,
    temp_C: 22
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const materials = ["PMMA", "PET-G", "HIPS", "PC"];

  // Funkcja sprawdzająca kompatybilność lokalnie
  const checkLocalCompatibility = () => {
    if (!form.adhesiveId) return null;
    
    const [mat1, mat2] = form.materials;
    
    // Kleje tylko do PMMA
    const pmmaOnlyAdhesives = ["dcm", "acrifix_1r0192", "weldon_16", "cosmofen_pmma"];
    
    if (pmmaOnlyAdhesives.includes(form.adhesiveId)) {
      if (mat1 !== "PMMA" || mat2 !== "PMMA") {
        return {
          error: `Ten klej może być używany WYŁĄCZNIE do klejenia PMMA z PMMA!`,
          suggestions: ["ATK-16 UV", "ATK UV10", "Permabond UV639", "Technicoll 8008"]
        };
      }
    }
    
    // Kleje UV - ostrzeżenie dla PC/PET-G
    const uvAdhesives = ["atk16_uv", "atk_uv10", "atk_uv14", "deskglue_uv5000", "deskglue_uv7000", "permabond_uv639"];
    if (uvAdhesives.includes(form.adhesiveId)) {
      if (mat1 === "PC" || mat2 === "PC" || mat1 === "PET-G" || mat2 === "PET-G") {
        return {
          warning: "Dla PC/PET-G wykonaj próbę na odpadzie materiału przed finalnym klejeniem."
        };
      }
    }
    
    return null;
  };

  const compatibility = checkLocalCompatibility();

  const submit = async (e: any) => {
    e.preventDefault();
    
    if (!form.adhesiveId) {
      setError("Wybierz klej");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/adhesives/check", { 
        method: "POST", 
        headers: { "Content-Type": "application/json" }, 
        body: JSON.stringify(form) 
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Błąd podczas obliczeń");
      }
      
      const data = await res.json();
      onChecked(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {error && (
        <div className="bg-red-500/20 border border-red-500 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
          <span className="text-sm text-red-400">{error}</span>
        </div>
      )}

      {/* Wybór kleju */}
      <div>
        <label className="block text-sm font-medium mb-2 text-zinc-300">Klej</label>
        <select 
          className="w-full bg-zinc-700 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:outline-none"
          value={form.adhesiveId} 
          onChange={e => setForm({...form, adhesiveId: e.target.value})}
        >
          <option value="">— wybierz klej —</option>
          {adhesives.map((a:any) => {
            const label = a.subclass ? 
              `${a.name} (${a.class}-${a.subclass}) - ${a.vendor}` :
              `${a.name} (${a.class}) - ${a.vendor}`;
            return (
              <option key={a.id} value={a.id}>
                {label}
              </option>
            );
          })}
        </select>
      </div>

      {/* Ostrzeżenie o kompatybilności */}
      {compatibility?.error && (
        <div className="bg-red-500/20 border border-red-500 rounded-lg p-3">
          <p className="text-sm text-red-400 font-semibold">{compatibility.error}</p>
          {compatibility.suggestions && (
            <p className="text-xs text-red-300 mt-1">
              Sugerowane kleje: {compatibility.suggestions.join(", ")}
            </p>
          )}
        </div>
      )}
      {compatibility?.warning && (
        <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-3">
          <p className="text-sm text-yellow-400">{compatibility.warning}</p>
        </div>
      )}

      {/* Wybór materiałów */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium mb-2 text-zinc-300">Materiał 1</label>
          <select 
            className="w-full bg-zinc-700 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:outline-none"
            value={form.materials[0]} 
            onChange={e => setForm({...form, materials:[e.target.value, form.materials[1]]})}
          >
            {materials.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2 text-zinc-300">Materiał 2</label>
          <select 
            className="w-full bg-zinc-700 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:outline-none"
            value={form.materials[1]} 
            onChange={e => setForm({...form, materials:[form.materials[0], e.target.value]})}
          >
            {materials.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Parametry geometryczne */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-2 text-zinc-300">
            Grubość [mm]
          </label>
          <input 
            type="number" 
            step="0.1" 
            className="w-full bg-zinc-700 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:outline-none"
            value={form.thickness_mm} 
            onChange={e => setForm({...form, thickness_mm: +e.target.value})}
            min="0.1"
            max="50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2 text-zinc-300">
            Szerokość spoiny [mm]
          </label>
          <input 
            type="number" 
            className="w-full bg-zinc-700 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:outline-none"
            value={form.bond_width_mm} 
            onChange={e => setForm({...form, bond_width_mm: +e.target.value})}
            min="1"
            max="5000"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2 text-zinc-300">
            Zakładka [mm]
          </label>
          <input 
            type="number" 
            className="w-full bg-zinc-700 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:outline-none"
            value={form.overlap_length_mm} 
            onChange={e => setForm({...form, overlap_length_mm: +e.target.value})}
            min="1"
            max="500"
          />
        </div>
      </div>

      {/* Obciążenie i temperatura */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium mb-2 text-zinc-300">
            Obciążenie F [N]
          </label>
          <input 
            type="number" 
            className="w-full bg-zinc-700 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:outline-none"
            value={form.load_N} 
            onChange={e => setForm({...form, load_N: +e.target.value})}
            min="1"
            max="100000"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2 text-zinc-300">
            Temperatura [°C]
          </label>
          <input 
            type="number" 
            className="w-full bg-zinc-700 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:outline-none"
            value={form.temp_C} 
            onChange={e => setForm({...form, temp_C: +e.target.value})}
            min="5"
            max="40"
          />
        </div>
      </div>

      {/* Przycisk oblicz */}
      <button 
        className="w-full bg-green-500 hover:bg-green-600 disabled:bg-zinc-600 text-white rounded-lg px-6 py-3 font-medium transition-colors flex items-center justify-center gap-2"
        type="submit"
        disabled={loading || !!compatibility?.error}
      >
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            Obliczanie...
          </>
        ) : (
          <>
            <Calculator className="w-5 h-5" />
            Oblicz parametry
          </>
        )}
      </button>
    </form>
  );
}
